import React, { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  Globe,
  MapPin,
  Plus,
  RotateCcw,
  Save,
  Sparkles,
  Type,
  X,
} from 'lucide-react';
import { peecAPI } from '../api/peecAPI';
import { useI18n } from '../i18n/useI18n';
import { Badge, PageHeader, PageShell, SectionCard } from '../components/PeecUI';

interface ProfilePageProps {
  workspaceId: string;
}

type BrandTargetMarket = string;

interface BrandProfileDraft {
  companyName: string;
  websiteUrl: string;
  description: string;
  industry: string;
  brandIdentity: string[];
  productsServices: string[];
  targetMarket: BrandTargetMarket;
  apiReference: {
    getEndpoint: string;
    putEndpoint: string;
    requestExample: string;
    responseExample: string;
  };
}

const TARGET_MARKET_CODES = [
  'AE', 'AR', 'AT', 'AU', 'BD', 'BE', 'BG', 'BH', 'BN', 'BO', 'BR', 'BY', 'CA', 'CH', 'CL', 'CN', 'CO', 'CR',
  'CY', 'CZ', 'DE', 'DK', 'DO', 'DZ', 'EC', 'EE', 'EG', 'ES', 'FI', 'FR', 'GB', 'GE', 'GH', 'GR', 'GT', 'HK',
  'HN', 'HR', 'HU', 'ID', 'IE', 'IL', 'IN', 'IQ', 'IS', 'IT', 'JM', 'JO', 'JP', 'KE', 'KH', 'KR', 'KW', 'KZ',
  'LB', 'LK', 'LT', 'LU', 'LV', 'MA', 'MD', 'ME', 'MK', 'MM', 'MO', 'MT', 'MX', 'MY', 'NG', 'NI', 'NL', 'NO',
  'NP', 'NZ', 'OM', 'PA', 'PE', 'PH', 'PK', 'PL', 'PR', 'PT', 'PY', 'QA', 'RO', 'RS', 'SA', 'SE', 'SG', 'SI',
  'SK', 'SV', 'TH', 'TN', 'TR', 'TW', 'UA', 'US', 'UY', 'VE', 'VN', 'ZA',
] as const;

const LEGACY_TARGET_MARKET_MAP: Record<string, string> = {
  us: 'US',
  uk: 'GB',
  eu: 'DE',
  apac: 'SG',
  mena: 'AE',
  latam: 'BR',
};

const INDUSTRY_OPTIONS = [
  'profile.industryOptions.saas',
  'profile.industryOptions.fintech',
  'profile.industryOptions.ecommerce',
  'profile.industryOptions.healthcare',
  'profile.industryOptions.agency',
  'profile.industryOptions.other',
] as const;

const normalizeWebsiteUrl = (value: string) => {
  if (!value.trim()) {
    return '';
  }

  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const extractDomain = (value: string) => {
  const normalized = normalizeWebsiteUrl(value);
  if (!normalized) {
    return '';
  }

  try {
    return new URL(normalized).hostname;
  } catch {
    return normalized.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  }
};

const normalizeTargetMarket = (value: BrandTargetMarket) => {
  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return 'US';
  }

  const legacyValue = LEGACY_TARGET_MARKET_MAP[normalizedValue.toLowerCase()];
  if (legacyValue) {
    return legacyValue;
  }

  return normalizedValue.toUpperCase();
};

const createFlagEmoji = (countryCode: string) => {
  if (!/^[A-Z]{2}$/.test(countryCode)) {
    return '';
  }

  return String.fromCodePoint(...countryCode.split('').map((char) => 127397 + char.charCodeAt(0)));
};

const createInitialDraft = (workspaceId: string): BrandProfileDraft => {
  return {
    companyName: '',
    websiteUrl: '',
    description: '',
    industry: 'profile.industryOptions.other',
    brandIdentity: [],
    productsServices: [],
    targetMarket: 'US',
    apiReference: {
      getEndpoint: `GET /workspace/${workspaceId}/profile`,
      putEndpoint: `PUT /workspace/${workspaceId}/profile`,
      requestExample: '{}',
      responseExample: '{}',
    },
  };
};

const LabelEditor: React.FC<{
  title: string;
  subtitle: string;
  values: string[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onAdd: () => void;
  onRemove: (value: string) => void;
  placeholder: string;
}> = ({ title, subtitle, values, inputValue, onInputChange, onAdd, onRemove, placeholder }) => (
  <div className="rounded-2xl border border-zinc-200 bg-zinc-50/70 p-4">
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="text-sm font-semibold text-zinc-950">{title}</h3>
        <p className="mt-1 text-xs leading-5 text-zinc-500">{subtitle}</p>
      </div>
      <Badge tone="slate">{values.length}</Badge>
    </div>

    <div className="mt-4 flex flex-wrap gap-2">
      {values.map((value) => (
        <span
          key={value}
          className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-700"
        >
          {value}
          <button
            type="button"
            aria-label={`Remove ${value}`}
            onClick={() => onRemove(value)}
            className="rounded-full text-zinc-400 outline-none transition-colors hover:text-zinc-700 focus-visible:ring-2 focus-visible:ring-zinc-400"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </span>
      ))}
    </div>

    <div className="mt-4 flex gap-2">
      <input
        name={`${title}-label-input`}
        autoComplete="off"
        type="text"
        value={inputValue}
        onChange={(event) => onInputChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            onAdd();
          }
        }}
        placeholder={placeholder}
        className="h-11 flex-1 rounded-lg border border-zinc-200 bg-white px-3 text-sm text-zinc-950 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
      />
      <button
        type="button"
        onClick={onAdd}
        className="inline-flex h-11 items-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-700 outline-none transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
      >
        <Plus size={16} aria-hidden="true" />
        Add
      </button>
    </div>
  </div>
);

const ProfilePage: React.FC<ProfilePageProps> = ({ workspaceId }) => {
  const { language, t } = useI18n();
  const [savedDraft, setSavedDraft] = useState<BrandProfileDraft>(() => createInitialDraft(workspaceId));
  const [draft, setDraft] = useState<BrandProfileDraft>(() => createInitialDraft(workspaceId));
  const [brandIdentityInput, setBrandIdentityInput] = useState('');
  const [productsInput, setProductsInput] = useState('');
  const [saveState, setSaveState] = useState<'idle' | 'saved'>('idle');
  const [aiGenerated, setAiGenerated] = useState(false);

  useEffect(() => {
    peecAPI.getBrandProfile(workspaceId)
      .then((profile: any) => {
        const nextDraft = profile
          ? { ...profile, targetMarket: normalizeTargetMarket(profile.targetMarket) }
          : createInitialDraft(workspaceId);
        setSavedDraft(nextDraft);
        setDraft(nextDraft);
        setBrandIdentityInput('');
        setProductsInput('');
        setSaveState('idle');
        setAiGenerated(false);
      })
      .catch((error) => console.error('Failed to fetch brand profile:', error));
  }, [workspaceId]);

  const domain = useMemo(() => extractDomain(draft.websiteUrl), [draft.websiteUrl]);
  const faviconUrl = domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=128` : '';
  const previewUrl = normalizeWebsiteUrl(draft.websiteUrl);
  const regionNames = useMemo(
    () => new Intl.DisplayNames([language === 'zh' ? 'zh-Hans' : 'en'], { type: 'region' }),
    [language],
  );
  const targetMarketOptions = useMemo(
    () => TARGET_MARKET_CODES
      .map((code) => ({
        id: code,
        flag: createFlagEmoji(code),
        label: regionNames.of(code) ?? code,
      }))
      .sort((left, right) => left.label.localeCompare(right.label, language === 'zh' ? 'zh-Hans' : 'en')),
    [language, regionNames],
  );
  const selectedTargetMarket = targetMarketOptions.find((market) => market.id === draft.targetMarket);

  const updateField = <K extends keyof BrandProfileDraft>(field: K, value: BrandProfileDraft[K]) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setSaveState('idle');
  };

  const addLabel = (field: 'brandIdentity' | 'productsServices', value: string) => {
    const normalizedValue = value.trim();
    if (!normalizedValue) {
      return;
    }

    setDraft((current) => {
      if (current[field].includes(normalizedValue)) {
        return current;
      }

      return {
        ...current,
        [field]: [...current[field], normalizedValue],
      };
    });
    setSaveState('idle');
  };

  const removeLabel = (field: 'brandIdentity' | 'productsServices', value: string) => {
    setDraft((current) => ({
      ...current,
      [field]: current[field].filter((item) => item !== value),
    }));
    setSaveState('idle');
  };

  const handleGenerateDescription = () => {
    const marketLabel = selectedTargetMarket ? selectedTargetMarket.label : t('profile.defaultTargetMarket');
    const identityText = draft.brandIdentity.join(language === 'zh' ? '、' : ', ');
    const productsText = draft.productsServices.join(language === 'zh' ? '、' : ', ');

    const generatedDescription = language === 'zh'
      ? `${draft.companyName} 面向${marketLabel}市场，专注于${t(draft.industry).toLowerCase()}，提供${productsText}。品牌表达应保持${identityText}，让 AI 与用户都能快速理解其价值、定位与差异化。`
      : `${draft.companyName} serves the ${marketLabel} market in ${t(draft.industry).toLowerCase()}, offering ${productsText}. Its brand voice should stay ${identityText}, so both buyers and AI systems can quickly understand the company's value, positioning, and differentiation.`;

    updateField('description', generatedDescription);
    setAiGenerated(true);
  };

  const handleSave = () => {
    peecAPI.updateBrandProfile(workspaceId, draft as unknown as Record<string, unknown>)
      .then(() => {
        setSavedDraft(draft);
        setSaveState('saved');
        setAiGenerated(false);
      })
      .catch((error) => console.error('Failed to save brand profile:', error));
  };

  const handleRefresh = () => {
    setDraft(savedDraft);
    setBrandIdentityInput('');
    setProductsInput('');
    setAiGenerated(false);
    setSaveState('idle');
  };

  return (
    <PageShell>
      <div className="mb-8">
        <PageHeader title={t('profile.title')} eyebrow={t('sidebar.company')} />
        <p className="-mt-3 max-w-3xl text-sm leading-6 text-zinc-500">{t('profile.subtitle')}</p>
      </div>

      <SectionCard
        title={t('profile.brandOverview')}
        subtitle={t('profile.brandOverviewSubtitle')}
        action={
          <div className="flex items-center gap-2">
            {aiGenerated ? <Badge tone="amber">{t('profile.aiGenerated')}</Badge> : null}
            {saveState === 'saved' ? <Badge tone="green">{t('profile.savedState')}</Badge> : null}
          </div>
        }
        className="mb-6"
      >
        <div className="grid gap-6 px-5 py-5 xl:grid-cols-[72px_minmax(0,1fr)_280px]">
          <div className="flex items-start pt-1">
            {faviconUrl ? (
              <img src={faviconUrl} alt={draft.companyName} className="h-16 w-16 rounded-2xl object-contain" />
            ) : (
              <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-100 text-xl font-semibold text-zinc-700">
                {draft.companyName.slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <h2 className="text-balance text-3xl font-semibold tracking-tight text-zinc-950">{draft.companyName}</h2>
              <a
                href={previewUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-2 text-sm text-zinc-500 outline-none transition-colors hover:text-zinc-800 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
              >
                <Globe size={15} aria-hidden="true" />
                {previewUrl}
              </a>
            </div>

            <p className="max-w-3xl text-sm leading-7 text-zinc-600">{draft.description}</p>

            <div className="flex flex-wrap gap-2">
              <Badge tone="slate">{t(draft.industry)}</Badge>
              {selectedTargetMarket ? <Badge tone="slate">{selectedTargetMarket.flag} {selectedTargetMarket.label}</Badge> : null}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
              <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-400">{t('profile.domain')}</div>
              <div className="mt-2 break-all text-sm font-semibold text-zinc-950">{domain}</div>
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
              <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-400">{t('profile.brandIdentity')}</div>
              <div className="mt-2 text-sm font-semibold text-zinc-950">{draft.brandIdentity.length}</div>
            </div>
            <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3">
              <div className="text-[11px] font-medium uppercase tracking-wider text-zinc-400">{t('profile.productsServices')}</div>
              <div className="mt-2 text-sm font-semibold text-zinc-950">{draft.productsServices.length}</div>
            </div>
          </div>
        </div>
      </SectionCard>

      <div className="space-y-6">
        <SectionCard title={t('profile.brandBasics')} subtitle={t('profile.brandBasicsSubtitle')}>
            <div className="grid gap-4 px-5 py-5 lg:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="company-name" className="block text-[11px] font-medium uppercase tracking-wider text-zinc-400">{t('profile.companyName')}</label>
                <div className="relative">
                  <Type size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" aria-hidden="true" />
                  <input
                    id="company-name"
                    name="companyName"
                    autoComplete="organization"
                    type="text"
                    value={draft.companyName}
                    onChange={(event) => updateField('companyName', event.target.value)}
                    placeholder={t('profile.companyNamePlaceholder')}
                    className="h-11 w-full rounded-lg border border-zinc-200 bg-white pl-10 pr-3 text-sm text-zinc-950 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="brand-website-url" className="block text-[11px] font-medium uppercase tracking-wider text-zinc-400">{t('profile.websiteUrl')}</label>
                <div className="relative">
                  <Globe size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" aria-hidden="true" />
                  <input
                    id="brand-website-url"
                    name="websiteUrl"
                    autoComplete="url"
                    type="url"
                    inputMode="url"
                    spellCheck={false}
                    value={draft.websiteUrl}
                    onChange={(event) => updateField('websiteUrl', event.target.value)}
                    placeholder={t('profile.websiteUrlPlaceholder')}
                    className="h-11 w-full rounded-lg border border-zinc-200 bg-white pl-10 pr-3 text-sm text-zinc-950 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="industry" className="block text-[11px] font-medium uppercase tracking-wider text-zinc-400">{t('profile.industry')}</label>
                <div className="relative">
                  <Building2 size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" aria-hidden="true" />
                  <select
                    id="industry"
                    name="industry"
                    aria-label={t('profile.industry')}
                    value={draft.industry}
                    onChange={(event) => updateField('industry', event.target.value)}
                    className="h-11 w-full rounded-lg border border-zinc-200 bg-white pl-10 pr-3 text-sm text-zinc-950 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                  >
                    {INDUSTRY_OPTIONS.map((option) => (
                      <option key={option} value={option}>{t(option)}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label htmlFor="target-market" className="block text-[11px] font-medium uppercase tracking-wider text-zinc-400">{t('profile.targetMarkets')}</label>
                <div className="relative">
                  <MapPin size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" aria-hidden="true" />
                  <select
                    id="target-market"
                    name="targetMarket"
                    aria-label={t('profile.targetMarkets')}
                    value={draft.targetMarket}
                    onChange={(event) => updateField('targetMarket', event.target.value)}
                    className="h-11 w-full rounded-lg border border-zinc-200 bg-white pl-10 pr-3 text-sm text-zinc-950 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                  >
                    {targetMarketOptions.map((market) => (
                      <option key={market.id} value={market.id}>{market.flag} {market.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
        </SectionCard>

        <SectionCard title={t('profile.brandNarrative')} subtitle={t('profile.brandNarrativeSubtitle')}>
          <div className="space-y-5 px-5 py-5">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label htmlFor="brand-description" className="block text-[11px] font-medium uppercase tracking-wider text-zinc-400">{t('profile.description')}</label>
                  <button
                    type="button"
                    onClick={handleGenerateDescription}
                    aria-label={t('profile.generateDescription')}
                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-700 outline-none transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                  >
                    <Sparkles size={15} aria-hidden="true" />
                    {t('profile.generateDescription')}
                  </button>
                </div>
                <textarea
                  id="brand-description"
                  name="description"
                  rows={5}
                  value={draft.description}
                  onChange={(event) => updateField('description', event.target.value)}
                  placeholder={t('profile.descriptionPlaceholder')}
                  className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-3 text-sm leading-7 text-zinc-950 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                />
              </div>

              <div className="grid gap-4 xl:grid-cols-2">
                <LabelEditor
                  title={t('profile.brandIdentity')}
                  subtitle={t('profile.brandIdentityHelper')}
                  values={draft.brandIdentity}
                  inputValue={brandIdentityInput}
                  onInputChange={setBrandIdentityInput}
                  onAdd={() => {
                    addLabel('brandIdentity', brandIdentityInput);
                    setBrandIdentityInput('');
                  }}
                  onRemove={(value) => removeLabel('brandIdentity', value)}
                  placeholder={t('profile.brandIdentityPlaceholder')}
                />
                <LabelEditor
                  title={t('profile.productsServices')}
                  subtitle={t('profile.productsServicesHelper')}
                  values={draft.productsServices}
                  inputValue={productsInput}
                  onInputChange={setProductsInput}
                  onAdd={() => {
                    addLabel('productsServices', productsInput);
                    setProductsInput('');
                  }}
                  onRemove={(value) => removeLabel('productsServices', value)}
                  placeholder={t('profile.productsServicesPlaceholder')}
                />
              </div>
            </div>
        </SectionCard>

        <SectionCard title={t('profile.contentChecklist')} subtitle={t('profile.contentChecklistSubtitle')}>
          <div className="space-y-3 px-5 py-5 text-sm text-zinc-600">
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3">
                <div className="font-medium text-zinc-950">{t('profile.checklistPointOneTitle')}</div>
                <p className="mt-1 leading-6 text-zinc-500">{t('profile.checklistPointOneBody')}</p>
              </div>
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3">
                <div className="font-medium text-zinc-950">{t('profile.checklistPointTwoTitle')}</div>
                <p className="mt-1 leading-6 text-zinc-500">{t('profile.checklistPointTwoBody')}</p>
              </div>
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3">
                <div className="font-medium text-zinc-950">{t('profile.checklistPointThreeTitle')}</div>
                <p className="mt-1 leading-6 text-zinc-500">{t('profile.checklistPointThreeBody')}</p>
              </div>
            </div>
        </SectionCard>

        <div className="flex flex-col items-start gap-3 rounded-xl bg-white px-5 py-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-md text-xs leading-5 text-zinc-400">{t('profile.refreshHint')}</p>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              aria-label={t('profile.refresh')}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-zinc-200 bg-white px-4 text-sm font-medium text-zinc-700 outline-none transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
            >
              <RotateCcw size={16} aria-hidden="true" />
              {t('profile.refresh')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              aria-label={t('profile.save')}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-zinc-950 px-4 text-sm font-medium text-white outline-none transition-colors hover:bg-zinc-800 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
            >
              <Save size={16} aria-hidden="true" />
              {t('profile.save')}
            </button>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default ProfilePage;
